import { GenericAbortSignal } from 'axios';

import { HighCostMedicationListItem } from 'models/HighCostMedicationListItem';
import { Paginated } from 'models/Paginated';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export type AdminHighCostMedicationListItemsIndexParams = BaseIndexQueryParams;

export const adminHighCostMedicationListItemQueryKeys = {
  base: ['adminHighCostMedicationListItems'],
  show: (id: string) => ['adminHighCostMedicationListItems', 'show', id],
  index: (listId: string, params: Record<string, any> = {}) => [
    'adminHighCostMedicationListItems',
    'index',
    listId,
    params,
  ],
};

export async function indexAdminHighCostMedicationListItems(
  listId: string,
  params: AdminHighCostMedicationListItemsIndexParams,
  signal?: GenericAbortSignal
) {
  return http
    .get<
      Paginated<HighCostMedicationListItem>
    >(`/admin/high_cost_medication_lists/${listId}/high_cost_medication_list_items`, { params, signal })
    .then((response) => response.data);
}

// Resolves one medication by id, for a deep link to its edit form: the slideout's table paginates,
// so a linked medication may sit on a page the browser never fetched.
export async function showAdminHighCostMedicationListItem(id: string, signal?: GenericAbortSignal) {
  return http
    .get<HighCostMedicationListItem>(`/admin/high_cost_medication_list_items/${id}`, { signal })
    .then((response) => response.data);
}

// The six writable fields. genericName is the only one the API requires; brandName is a match key
// olio-ai compares against and the rest is reviewer context, so all five are nullable. See design 001.
//
// costTier is the tier's free-text label rather than an id: the cost tier key is a rollup of what
// the medications carry, so sending a label the list has never seen creates the tier. See design 002.
export type AdminHighCostMedicationListItemMutationParams = {
  genericName: string;
  brandName: string | null;
  costTier: string | null;
  dosage: string | null;
  route: string | null;
  form: string | null;
};

// clientId is create-only: the API resolves the client's active list from it. An edit acts on a
// medication id and must not be able to reparent the row.
export type AdminHighCostMedicationListItemCreateParams = AdminHighCostMedicationListItemMutationParams & {
  clientId: string;
};

export async function createAdminHighCostMedicationListItem(params: AdminHighCostMedicationListItemCreateParams) {
  return http
    .post<HighCostMedicationListItem>('/admin/high_cost_medication_list_items', params)
    .then((response) => response.data);
}

export async function updateAdminHighCostMedicationListItem({
  id,
  ...params
}: AdminHighCostMedicationListItemMutationParams & { id: string }) {
  return http
    .patch<HighCostMedicationListItem>(`/admin/high_cost_medication_list_items/${id}`, params)
    .then((response) => response.data);
}

// Soft-archives the medication; nothing is hard-deleted. The medication count and the AI check stop
// counting it immediately.
export async function destroyAdminHighCostMedicationListItem({ id }: { id: string }) {
  return http.delete(`/admin/high_cost_medication_list_items/${id}`);
}
